import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import IconProvider from "@/components/ui/IconProvider";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface TranslationWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (action: 'remove' | 'unapprove') => void;
  onCancel: () => void;
  translationCount: number;
}

const TranslationWarningDialog: React.FC<TranslationWarningDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  onCancel,
  translationCount,
}) => {
  const { t } = usePlatformTranslation();
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-auto">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 text-amber-500 dark:text-amber-400">
              <IconProvider
                iconstring="fa-triangle-exclamation"
                size={24}
              />
            </div>
            <AlertDialogTitle className="text-lg font-semibold text-foreground">
              Existing Translations Found
            </AlertDialogTitle>
          </div>
        </AlertDialogHeader>
        
        <div className="text-sm text-muted-foreground space-y-3">
          <p>
            This source text has <strong className="text-foreground">{translationCount} existing translation(s)</strong>. 
            Modifying the source text will likely require re-translation of these texts.
          </p>
          
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-md p-3">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
              Choose how to handle existing translations:
            </p>
            <div className="space-y-2 text-sm text-amber-700 dark:text-amber-300">
              <div className="flex items-start gap-2">
                <IconProvider iconstring="fa-trash" size={14} className="text-amber-600 dark:text-amber-400" />
                <span><strong>{t('platform.cards.widgets.remove_translations')}</strong> Clear all translated text (set to empty)</span>
              </div>
              <div className="flex items-start gap-2">
                <IconProvider iconstring="fa-times-circle" size={14} className="text-amber-600 dark:text-amber-400" />
                <span><strong>{t('platform.cards.widgets.unapprove_translations')}</strong> Keep text but mark as not approved</span>
              </div>
            </div>
          </div>
        </div>
        
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel 
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancel
          </AlertDialogCancel>
          <div className="flex gap-2 w-full sm:w-auto">
            <AlertDialogAction
              onClick={() => onConfirm('unapprove')}
              className="bg-amber-600 hover:bg-amber-700 dark:bg-amber-600 dark:hover:bg-amber-500 focus:ring-amber-500 w-full sm:w-auto"
            >
              Unapprove Translations
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => onConfirm('remove')}
              className="bg-destructive hover:bg-destructive/90 focus:ring-destructive w-full sm:w-auto"
            >
              Remove Translations
            </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TranslationWarningDialog; 